import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Collapsible,
  CollapsiblePanel,
  Icon,
  ScrollArea,
  Separator,
  Text,
  toastManager,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  WidgetEmpty,
  WidgetGrid,
  clampRectToSpan,
  defaultRowsForSpan,
  packLayout,
  presetInitialConfig,
  useWidgetRegistry,
  useGridHistoryHotkeys,
  useWidgetBoardHistory,
  WIDGET_PRESET_KEY,
  type GridLayoutItem,
  type GridRect,
  type WidgetConfig,
  type WidgetGridAreaAdd,
  type WidgetInstance,
  type WidgetMove,
  type WidgetPreset,
} from "@salestrak/ui";
import { AddWidgetDialog } from "#app/widgets/dashboards/settings/add-widget-dialog";
import type { DashboardTemplate } from "#app/widgets/dashboards/settings/dashboard-templates";
import { DashboardTemplateChooser } from "#app/widgets/dashboards/settings/dashboard-template-chooser";
import { ManageWidgetsPanel } from "#app/widgets/dashboards/settings/manage-widgets-panel";
import { WidgetDetailDialog } from "#app/widgets/dashboards/widget-detail-dialog";
import { WidgetDrilldownProvider } from "#app/widgets/dashboards/widget-drilldown";
import {
  GRID_COLUMNS,
  useWidgetView,
  type WidgetSeed,
} from "#app/widgets/dashboards/use-widget-view";
import { useWidgetViewAccess } from "#app/widgets/dashboards/use-widget-view-access";
import { useManagedBoardMaintenance } from "#app/widgets/dashboards/use-managed-board-maintenance";
import { useManagedBoardCallbacks } from "#app/widgets/dashboards/use-managed-board-callbacks";
import { useDashboardOverlays } from "#app/widgets/dashboards/use-dashboard-overlays";
import type { ManagedWidget } from "#app/widgets/dashboards/managed-widgets";
import { useWidgetRefresh, WidgetRefreshBoundary } from "#app/widgets/dashboards/use-widget-refresh";
import {
  HALF_FULL_WIDTH_OPTIONS,
  usesHalfFullWidths,
} from "#app/widgets/dashboards/managed-surface";
import { SaveViewControl } from "#app/components/view-options";
import { DashboardViewOptions } from "#app/widgets/dashboards/dashboard-view-options";
import {
  DashboardGreeting,
  DashboardLayoutPopover,
  DashboardMastheadChrome,
  DashboardTeamLayoutBanner,
  FreeToolbarChrome,
  MANAGED_HOME_CONTENT_MAX_WIDTH,
} from "#app/widgets/dashboards/surface-chrome";
import { useMyWorkGreetingVisible } from "#app/widgets/dashboards/use-my-work-greeting";
import { DASHBOARD_ROW_HEIGHT, DashboardBoardSkeleton,
  DASHBOARD_GRID_GAP,
  DEFAULT_WIDGET_ROWS,
} from "#app/widgets/dashboards/dashboard-board-skeleton";
import { resolveWidgetViewMode } from "#app/widgets/dashboards/widget-view-mode";
import { isSharedTier } from "#app/widgets/dashboards/widget-view-policy";
import { parsePersonalSnapshot } from "#app/widgets/dashboards/widget-view-snapshot";
import { DashboardScopeProvider } from "#app/widgets/dashboards";
import {
  useDashboardScopeState,
  type DashboardEntityScope,
} from "#app/widgets/dashboards/scope/use-dashboard-scope-state";
import { ViewOptionsPanel } from "#app/components/view-options";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { scopeEntityId, scopeWhere, type IssueScope } from "#app/lib/filters/issue-scope";
import { useIssueFiltersOptional } from "#app/lib/filters/issue-filter-context";
import { useViewsOptional } from "#app/lib/views/view-context";
import { viewPath } from "#app/lib/views/view-routes";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { VIEW_PANE_ANCHOR_ID } from "#app/lib/hooks/use-anchor-insets";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";
import { combineWhereConditions } from "#app/lib/query-utils";
import { useAnalytics, AnalyticsContext, WidgetViewEvent } from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";
import { createEditSessionGate } from "#app/lib/analytics/edit-session";

// A held ⌘Z repeats; count one undo (or redo) per burst.
const historyBurst = createEditSessionGate(2_000);

/**
 * The `Dashboard` host: the library `WidgetGrid` + the surrounding chrome
 * (`surface-chrome.tsx` — one of three headers per surface, empty states) +
 * the app-owned `AddWidgetDialog` gallery and the `WidgetDetailDialog` editor
 * (whose Settings drawer is THE widget settings surface — the gear and
 * Maximize both open it), over the widget view store (`useWidgetView`), with
 * every widget self-fetching through the ambient dashboard scope. The host
 * also provides the drilldown channel: a chart body publishes a clicked
 * slice through `useWidgetDrilldown`, and the detail dialog opens on its
 * Data tab filtered to it. Hosted by `/my-work` (member-scoped), Team
 * Overview, the workflow Dashboard tab + saved dashboard views
 * (entity-scoped), and the workspace dashboards page (`/dashboards/$viewId`,
 * scoped to a workflow set).
 *
 * This is the DASHBOARD domain's host — it owns its own chrome and copy so
 * the library kit stays domain-free (a future domain can compose
 * `WidgetGrid` with its own chrome the same way).
 *
 * Widgets persist through the core GraphQL widgets API keyed by `(workspace,
 * view)`. Who may edit is the OWNERSHIP model (`useWidgetViewAccess` — the
 * same shared grammar the server enforces), and it is ONE model for every
 * view: a view has a shared/default board, ANY viewer may hold a personal
 * fork of it (a private settings snapshot that never writes widget rows),
 * and WHICH board this surface shows is decided by state, never a toggle —
 * no fork means the shared board, a fork means the fork
 * (`resolveWidgetViewMode`). The only permission question is who edits the
 * SHARED board: `widgets.manage` holders and that team's managers, whose
 * shared-board edits STAGE behind the toolbar's save control; everyone
 * else's first edit quietly forks (announced by a one-time toast). My Work
 * is the private-owner special case — the owner edits their board directly
 * (autosave, no fork) and can reset it back to the catalog's default seed.
 * Team Overview is the personal-only shared exception: it reads the
 * canonical board as its starting point, then sends every edit to the
 * viewer's settings fork. Other shared surfaces put the board actions (Auto
 * layout, Reset to team layout, Edit team layout) in the
 * Customize view sections (`dashboard-view-options.tsx`) behind the gear,
 * and Save/Discard in the toolbar's staged-save control (the shared
 * `SaveViewControl`).
 *
 * The `IssueScope` pins the dashboard to one workflow, team, or member: its
 * `scopeWhere` predicate is AND-merged into every widget's query (via
 * `useScopedWhere`), and the dashboard gets its own view key per entity.
 * Feature-flag gating lives in the host route, not here.
 * Pass `surfaceVariant` for a distinct persisted view under the same scope.
 *
 * Pass `managedCatalog` to make this a **managed** surface (My Work, Team
 * Overview): a fixed, curated set of predefined widgets the viewer only
 * toggles on/off — NOT a free add-any-widget gallery. The designed x/y/w/h
 * are the starting layout; editors can rearrange/resize, and the toggle
 * panel remains the only add/remove control. Every view's default board —
 * managed default-on set or a free dashboard's default template — is seeded
 * SERVER-SIDE on the view's first read (core's widget-view-defaults.ts), so
 * boards exist before any client logic runs. A titled managed surface gets
 * the standard pinned page header (`title` + scope controls + Customize);
 * My Work adds a greeting line that scrolls with the board.
 */

function greetingLabel(name?: string | null) {
  const hour = new Date().getHours();
  const period = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  const firstName = name?.trim().split(/\s+/)[0];

  return firstName ? `Good ${period}, ${firstName}` : `Good ${period}`;
}

export type DashboardProps = Omittable<{
  scope: IssueScope;
  /** Optional masthead title for managed surfaces (for example, My Work). */
  title?: string;
  surfaceVariant?: string;
  /** The persisted widget-view key of a saved dashboard view. The server
   *  derives it on the row, so the board a view renders and the board the API
   *  accepts writes to are one value with one author. */
  viewKey?: string;
  managedCatalog?: ManagedWidget[];
  /** Connect managed chrome to a route tab with the same neutral surface. */
  tabLinkedChrome?: boolean;
  /** Keeps every shared-view edit in the viewer's private settings fork. */
  personalOnly?: boolean;
}>;

export function Dashboard({
  scope,
  title,
  surfaceVariant,
  viewKey,
  managedCatalog,
  tabLinkedChrome,
  personalOnly = false,
}: DashboardProps) {
  const { track } = useAnalytics();
  const { currentUser } = useAppData();
  // Stamp every widget event with the host's scope so the dataset is mineable:
  // "member" (my-work), or workflow/team for the Dashboard tabs.
  const widgetScope = scope.kind;
  const entityId = scopeEntityId(scope);
  // Which kind of board sent the event. Never the entity id: a workspace
  // dashboard's id is a joined list of workflow ids.
  const boardKind = managedCatalog
    ? "managed"
    : viewKey
      ? scope.kind === "workflows"
        ? "workspace"
        : "saved"
      : "builtin";

  // ONE envelope for every widget event — the WidgetView context + the host's
  // scope/board kind fold in here, so call sites carry only their own fields.
  const trackWidget = useCallback(
    (event: WidgetViewEvent, data?: Record<string, unknown>) =>
      track({
        context: AnalyticsContext.WidgetView,
        event,
        data: { scope: widgetScope, board: boardKind, ...data },
      }),
    [track, widgetScope, boardKind],
  );

  // The viewer's relationship to THIS view (the shared ownership grammar the
  // server guard also enforces): `canWrite` = the server accepts canonical
  // writes; `tier` + the fork state below pick the store mode and which
  // board actions the Customize view sections offer.
  const access = useWidgetViewAccess(scope, surfaceVariant, viewKey);
  // A share-only role (no public access) on a shared board: the widgets and
  // nothing else — no fork, no edit chrome, no transient controls. Every
  // gate below folds this in; the store never reads or writes a fork for it.
  const consumeOnly = access.consumeOnly;
  const { teamMap } = useWorkspaceData();

  // The saved-views scope, when this dashboard sits inside one (the workflow
  // Dashboard tab and saved dashboard views do; the managed hosts do not) —
  // it powers the staged-save menu's "Save as new tab".
  const views = useViewsOptional();
  const { replace } = useAppNavigate();

  // The screen's filter set — mounted by the workflow/team hosts (which wrap
  // their tabs in an `IssueFilterProvider`), absent on `/my-work`. Inside a
  // saved-view scope it IS the view's filters (the provider binds to the
  // config draft), so the toolbar chip and the Customize view Filters page
  // edit one set and this fold sees both. Its `where` AND-merges into the
  // entity scope below so the filters actually narrow every widget's data.
  const issueFilters = useIssueFiltersOptional();
  // Both layers — the view's own filters and the toolbar's temporary pool.
  const filterWhere = issueFilters?.where;
  const filterWhereKey = filterWhere ? JSON.stringify(filterWhere) : "";

  // Memoize on primitive deps (+ a stable serialization of the `where`) so
  // the published DashboardScope keeps a stable identity across renders (the
  // context value otherwise re-subscribes every widget).
  const entityScope = useMemo<DashboardEntityScope | null>(() => {
    const conditions = [scopeWhere(scope), filterWhere].filter(
      (c): c is Record<string, unknown> => Boolean(c),
    );
    return {
      entity: { type: scope.kind, id: scopeEntityId(scope) },
      entityWhere: combineWhereConditions(conditions) ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the scope kind and id, not the scope object
  }, [scope.kind, scopeEntityId(scope), filterWhereKey]);

  // The toolbar-driven data window every widget reports on. My Work
  // (member-scoped) is a personal home that must surface EVERYTHING assigned to
  // the viewer, so it alone opens on the unbounded all-time window. Team
  // Overview and the free dashboards open narrowed to the default this-week
  // preset — a dashboard reports on the current period first. The toolbar's
  // calendar chip rests icon-only on that default (and on all-time) and shows
  // a label only when the window deviates from them. Widgets that want a
  // narrower window (e.g. "Newly closed this week") carry their own per-widget
  // date override.
  const scopeState = useDashboardScopeState(
    entityScope,
    scope.kind === "member" ? { key: "all-time" } : undefined,
  );

  // A fork-holding writer's temporary jump back to the shared board ("Edit
  // team layout" behind the gear). Transient by design — never persisted, so
  // a reload lands back on the fork.
  const [editingTeamLayout, setEditingTeamLayout] = useState(false);

  // The viewer's fork (any shared view). The store only reads the fork once
  // the mode IS personal, so the host needs its own read to know a fork
  // exists at all — `useUserSetting`'s cache write-back keeps this second
  // consumer of the same key live with the store's fork writes and Reset.
  const fork = useUserSetting(`widget-view-personal:${access.view}`, parsePersonalSnapshot, {
    skip: !isSharedTier(access.tier) || consumeOnly,
  });
  const hasFork = fork.value !== null;

  // Auto layout and Auto refresh are properties of the VIEW, not of the
  // device: inside a saved view they draft into `ViewConfig` like every other
  // display option, preview immediately, and commit through the toolbar's
  // save control — so every member opens the tab packed and refreshing the
  // same way. Outside a saved-view scope there is no
  // row to carry them, so the same two switches fall back to a device-local
  // key per view; nothing to commit there, and no second save control.
  const inSavedView = Boolean(views?.activeView);
  const viewConfig = views?.config ?? null;
  const setViewConfig = views?.setConfig;
  const viewSave = views?.save;
  const viewDiscard = views?.discard;
  const { state: autoLayoutState, update: updateAutoLayout } = usePersistedState<{
    on: boolean;
  }>(`widget-view-auto-layout:${access.view}`, { on: false });
  const { state: autoRefreshState, update: updateAutoRefresh } = usePersistedState<{
    on: boolean;
  }>(`widget-view-auto-refresh:${access.view}`, { on: false });

  // Where writes go: a shared view's non-writer is always on their own copy
  // (`personal` — forkless it reads the shared board through the store's
  // canonical seed, and the first edit forks); a writer stages against the
  // shared board until they hold a fork, then lives on it; private views
  // (My Work) keep instant autosave — there is no blast radius. While the
  // fork read is unresolved a fork is ASSUMED: resolving personal-first
  // costs a forkless writer one pre-paint refetch (the store's personal
  // seeding waits on the fork read), where the opposite order would paint
  // the shared board and visibly swap it for the fork.
  const mode = resolveWidgetViewMode({
    tier: access.tier,
    canWrite: access.canWrite,
    hasFork: hasFork || fork.initialLoading,
    editingTeamLayout,
    personalOnly,
    consumeOnly,
  });
  const storeOpts = useMemo(() => ({ mode }), [mode]);
  const {
    widgets,
    initialLoading,
    fresh,
    addWidget,
    seedWidgets,
    duplicateWidget,
    replaceWidget,
    removeWidget,
    updateWidgetConfig,
    moveWidgets,
    restoreBoard,
    dirty,
    saveToTeam,
    discardChanges,
    saveToNewView,
    resetToDefault,
    direct,
  } = useWidgetView(access.view, storeOpts);

  // Effective edit — gates every edit affordance (masthead/toolbar buttons,
  // grid manage/arrange, hotkeys, empty states). Personal mode is ALWAYS
  // editable — edits land on the viewer's own copy, and forking needs no
  // permission — so every viewer of every surface may edit, except the
  // consume-only reader, who holds no copy to edit. Canonical-board
  // maintenance stays on `canMaintainCanonical` below.
  const canEdit = !consumeOnly && (access.canWrite || mode === "personal");

  // Managed surfaces hide the per-widget manage chrome (no gear/rename/
  // duplicate/delete — the toggle panel is the only add/remove surface) but
  // stay arrangeable: `canArrange` lets `widgets.manage` holders drag-reorder
  // and resize, and moves persist through `moveWidgets` like any view. A
  // catalog seeded on the half/full rhythm adds Half/Full presets to the
  // width menu; the drag itself commits at any span-legal width.
  const isManaged = !!managedCatalog;
  const halfFullWidths = usesHalfFullWidths(managedCatalog);

  // Free dashboards are always editable for a permitted viewer — there is no
  // edit mode. `canManageWidgets` is the RIGHT, and it alone arms the board's
  // edit affordances; unsaved shared edits surface through the toolbar's
  // staged-save control (the shared `SaveViewControl`).
  const canManageWidgets = isManaged ? false : canEdit;
  const gridCanManage = canManageWidgets;

  // Canonical-board maintenance runs only against the CANONICAL board: never
  // in personal mode (the effects would inspect/write the fork's rows). It also
  // stands down while staged edits are dirty: the corrections write through to
  // the server, but the frozen staged baseline would keep the corrected rows —
  // Discard would then resurrect server-deleted widgets. Maintenance defers
  // until the editor saves or discards (the effects dep on this gate, so
  // dirty clearing re-runs them against the re-synced board).
  const canMaintainCanonical = access.canWrite && mode !== "personal" && !dirty;
  const canMaintainManagedBoard = personalOnly ? mode === "personal" : canMaintainCanonical;

  const overlays = useDashboardOverlays();
  const {
    addOpen,
    replaceId,
    maximizedId,
    dataSlice,
    dataRequestId,
    settingsRequestId,
    chartRequestId,
    setAddOpen,
    setReplaceId,
    setMaximizedId,
    setDataSlice,
  } = overlays;
  // A ref keeps the drilldown handlers stable while the widget list changes.
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;
  const trackDetail = useCallback(
    (id: string, mode: "data" | "chart" | "maximize") =>
      trackWidget(WidgetViewEvent.OpenWidgetDetail, {
        type: widgetsRef.current.find((i) => i.id === id)?.type,
        mode,
      }),
    [trackWidget],
  );
  const { openData, openChart } = overlays;
  const drilldown = useMemo(
    () => ({
      openData: (...args: Parameters<typeof openData>) => {
        trackDetail(args[0], "data");
        openData(...args);
      },
      openChart: (id: string) => {
        trackDetail(id, "chart");
        openChart(id);
      },
    }),
    [openData, openChart, trackDetail],
  );
  const maximizedIndex = useMemo(
    () => (maximizedId ? widgets.findIndex((i) => i.id === maximizedId) : -1),
    [widgets, maximizedId],
  );
  const maximizedInstance = maximizedIndex >= 0 ? at(widgets, maximizedIndex, "widget") : null;
  const onMaximize = useCallback(
    (widget: WidgetInstance) => {
      trackDetail(widget.id, "maximize");
      setMaximizedId(widget.id);
    },
    [setMaximizedId, trackDetail],
  );

  const onMove = useCallback(
    (moves: WidgetMove[]) => {
      moveWidgets(moves);
      trackWidget(WidgetViewEvent.Rearrange, { count: moves.length });
    },
    [moveWidgets, trackWidget],
  );

  // Full-board undo/redo over the working copy (free dashboards — a managed
  // board's layout is catalog-designed, so it gets no history/Auto-layout).
  // Grid gestures persist through `onMove` and RECORD through the kit's
  // full-layout `onLayoutChange`; membership ops (add/duplicate/replace/
  // delete/seed) RECORD via `snapshot()` at their call sites; undo/redo and
  // the Auto-layout pack commit through the store's mode-routed
  // `restoreBoard`. The reset key carries the MODE too: a mode flip (the
  // first fork write, Reset, the Edit-team-layout jump) changes boards under
  // the same view key, and one board's snapshots must not restore onto the
  // other.
  const layout = useMemo<GridLayoutItem[]>(
    () => widgets.map((i) => ({ id: i.id, x: i.x, y: i.y, w: i.w, h: i.h })),
    [widgets],
  );
  const history = useWidgetBoardHistory({
    board: widgets,
    commit: restoreBoard,
    resetKey: `${access.view}:${mode}`,
  });
  const { undo: historyUndo, redo: historyRedo } = history;
  const undo = useCallback(() => {
    if (historyBurst("undo")) trackWidget(WidgetViewEvent.Undo);
    historyUndo();
  }, [historyUndo, trackWidget]);
  const redo = useCallback(() => {
    if (historyBurst("redo")) trackWidget(WidgetViewEvent.Redo);
    historyRedo();
  }, [historyRedo, trackWidget]);
  useGridHistoryHotkeys({
    undo,
    redo,
    enabled: canEdit && !isManaged,
  });
  // Auto layout: ON packs the board immediately (undoable) and holds the
  // grid's dense flow, so every later drag/resize/delete re-packs and holes
  // cannot form. OFF freezes positions where they sit — nothing moves back.
  // Managed boards never take dense flow; their layout is registry-owned.
  const autoLayout =
    !isManaged &&
    (inSavedView && viewConfig ? viewConfig.autoLayout : autoLayoutState.on);
  const onAutoLayoutChange = useCallback(
    (enabled: boolean) => {
      // The pack itself is a board edit — undoable, and it lands in whatever
      // the store's mode does with edits — while the flag that HOLDS the
      // board packed is a display option.
      if (enabled) history.apply(packLayout(layout, GRID_COLUMNS));
      if (inSavedView) setViewConfig?.({ autoLayout: enabled });
      else updateAutoLayout({ on: enabled });
      trackWidget(WidgetViewEvent.ToggleAutoLayout, { enabled });
    },
    [history, layout, inSavedView, setViewConfig, updateAutoLayout, trackWidget],
  );
  // Managed homes have no board-level refresh (`FreeToolbarChrome` is the
  // refresh control's only mount), so the flag is free-dashboard-only too.
  const autoRefresh =
    !isManaged &&
    (inSavedView && viewConfig ? viewConfig.autoRefresh : autoRefreshState.on);
  const onAutoRefreshChange = useCallback(
    (enabled: boolean) => {
      if (inSavedView) setViewConfig?.({ autoRefresh: enabled });
      else updateAutoRefresh({ on: enabled });
      trackWidget(WidgetViewEvent.ToggleAutoRefresh, { enabled });
    },
    [inSavedView, setViewConfig, updateAutoRefresh, trackWidget],
  );

  const onDelete = useCallback(
    (id: string) => {
      const type = widgets.find((i) => i.id === id)?.type;
      trackWidget(WidgetViewEvent.DeleteWidget, { type });
      history.snapshot();
      removeWidget(id);
    },
    [widgets, removeWidget, trackWidget, history],
  );

  // Marquee-to-add: a drawn rect opens the SAME Add-Widget gallery as the
  // toolbar button (never a parallel inline mini-picker) and places the pick
  // at the drawn cell AND size. The host holds the drawn rect so the grid
  // keeps it painted while the gallery is open; closing without a pick
  // clears it. Free dashboards only — a managed surface's catalog is fixed.
  const [marqueeArea, setMarqueeArea] = useState<GridRect | null>(null);
  const areaAdd = useMemo<WidgetGridAreaAdd>(
    () => ({
      pending: marqueeArea,
      onDraw: (area) => {
        setMarqueeArea(area);
        setAddOpen(true);
      },
    }),
    [marqueeArea, setAddOpen],
  );

  const onConfigure = useCallback(
    (widget: WidgetInstance) => {
      trackWidget(WidgetViewEvent.ConfigureWidget, { type: widget.type });
      // The gear opens the fullscreen detail dialog with its Settings drawer
      // open — that dialog is the one and only widget editor.
      overlays.openSettings(widget.id);
    },
    [trackWidget, overlays],
  );

  const onConfigChange = useCallback(
    (id: string, patch: WidgetConfig) => updateWidgetConfig(id, patch),
    [updateWidgetConfig],
  );

  const onDuplicate = useCallback(
    (widget: WidgetInstance) => {
      trackWidget(WidgetViewEvent.DuplicateWidget, { type: widget.type });
      history.snapshot();
      duplicateWidget(widget);
    },
    [duplicateWidget, trackWidget, history],
  );

  const onReplace = useCallback((widget: WidgetInstance) => setReplaceId(widget.id), [setReplaceId]);

  // Writing an explicit `null` stamp is the whole conversion: the config keeps
  // every value and only the binding goes. It must be `null`, not absent — an
  // unstamped config re-infers its preset from those very values.
  const onConvertToCustom = useCallback(
    (widget: WidgetInstance) => {
      trackWidget(WidgetViewEvent.ConvertToCustom, { type: widget.type });
      updateWidgetConfig(widget.id, { [WIDGET_PRESET_KEY]: null });
    },
    [updateWidgetConfig, trackWidget],
  );

  // Picking a template is the empty board's STARTING POINT, not an edit
  // staged against something the team can already see — so a writer's pick
  // writes straight through and the toolbar's Save control never appears for
  // it. Staged mode implies write access, so the mutations are always
  // accepted. The one exception is a board emptied by staged edits: its
  // frozen baseline still holds the widgets that were cleared, and writing
  // through past it would let Discard resurrect them beside the template's
  // rows — that pick stages like the clear it followed.
  const onSelectTemplate = useCallback(
    (seeds: WidgetSeed[], template: DashboardTemplate) => {
      trackWidget(WidgetViewEvent.ChooseStartingPoint, {
        template: template.key,
        seedCount: seeds.length,
      });
      history.snapshot();
      if (mode === "staged" && !dirty) direct.seed(seeds);
      else seedWidgets(seeds);
    },
    [history, mode, dirty, direct, seedWidgets, trackWidget],
  );
  // Scratch is a pick too: the gallery opens. Like a template, it is the
  // empty board's starting point, so the cards added from it write straight
  // through (while nothing else is staged) — staged, they were lost the
  // moment the writer left without "Save view". Keyed by view, so another
  // dashboard's scratch pick never carries over.
  const scratchViewRef = useRef<string | null>(null);
  const onStartFromScratch = useCallback(() => {
    trackWidget(WidgetViewEvent.ChooseStartingPoint, { template: "scratch", seedCount: 0 });
    scratchViewRef.current = access.view;
    setAddOpen(true);
  }, [setAddOpen, trackWidget, access.view]);
  const registry = useWidgetRegistry();
  const addFromScratch = useCallback(
    (preset: WidgetPreset, area: GridRect | undefined): boolean => {
      if (scratchViewRef.current !== access.view || mode !== "staged" || dirty) return false;
      const def = registry.get(preset.type);
      const span = def?.span ?? {};
      const w = preset.defaultW ?? 4;
      const h = def?.span.defaultH ?? defaultRowsForSpan(DEFAULT_WIDGET_ROWS, span);
      // The store's add settles the cell against the board; a write-through
      // seed names it, so take the drawn rect, else the first free row.
      const bottom = widgets.reduce((max, item) => Math.max(max, item.y + item.h), 0);
      const cell = area
        ? clampRectToSpan(area, span, GRID_COLUMNS)
        : { x: 0, y: bottom, w, h };
      direct.seed([{ type: preset.type, config: presetInitialConfig(preset), ...cell }]);
      return true;
    },
    [access.view, mode, dirty, registry, widgets, direct],
  );

  const {
    operations: managedMaintenanceOperations,
    onToggleManaged,
    renderManagedCard,
  } = useManagedBoardCallbacks({
    view: access.view,
    managed: isManaged,
    personalOnly,
    store: { seedWidgets, removeWidget, updateWidgetConfig, moveWidgets, direct },
    widgets,
    layout,
    trackWidget,
  });

  // Duplicate repair, the managed layout migration, and the gravity
  // self-heal — the canonical-board maintenance effects, in their required
  // order. (First-visit seeding is server-side: the widgets read lazily
  // seeds an uninitialized view's default board.)
  useManagedBoardMaintenance({
    managedCatalog,
    initialLoading,
    // `fresh` holds maintenance off a provisional cache-first paint — acting
    // on rows that lag this session's own writes would re-add widgets a
    // migration already placed.
    canMaintain: canMaintainManagedBoard && fresh,
    widgets,
    operations: managedMaintenanceOperations,
  });

  // The implicit fork: a non-writer's first edit quietly creates their copy
  // (the store's debounced snapshot — no permission wall, no arming step).
  // Announce the creation ONCE, on the RESOLVED fork read's false→true flip:
  // keying off resolved values means a fork holder's read arriving on mount
  // can never false-fire, and per-view tracking means switching dashboards
  // can't replay a stale transition. Writers are excluded — staged mode never
  // writes the fork, so a flip under a writer could only be another session's
  // doing and isn't this viewer's moment to announce.
  const forkSeenRef = useRef<{ view: string; hasFork: boolean } | null>(null);
  useEffect(() => {
    if (fork.initialLoading) return;
    const prev = forkSeenRef.current;
    forkSeenRef.current = { view: access.view, hasFork };
    if (!prev || prev.view !== access.view || prev.hasFork || !hasFork) return;
    if (!isSharedTier(access.tier) || personalOnly || access.canWrite || consumeOnly) return;
    trackWidget(WidgetViewEvent.Personalize);
    toastManager.add({
      title: "Customizing your own copy",
      description: "Reset to the team layout anytime from Customize view.",
      type: "info",
    });
  }, [
    fork.initialLoading,
    hasFork,
    access.view,
    access.tier,
    access.canWrite,
    personalOnly,
    consumeOnly,
    trackWidget,
  ]);

  // Reset to team layout = DELETE the fork. The store clears it and re-seeds
  // the working copy from the canonical rows, so the viewer is back on the
  // live shared board; their next edit forks again. For a writer the fork's
  // deletion also flips the resolved mode back to staged.
  const onResetPersonal = useCallback(() => {
    trackWidget(WidgetViewEvent.ResetPersonalLayout);
    resetToDefault();
  }, [trackWidget, resetToDefault]);

  // A dashboard tab has TWO halves a person can change — the widget board and
  // the view's display options — and to them it is one decision: keep what I
  // changed on this tab, or put it back. So one control commits both. Each
  // half is a no-op when it is clean (`saveToTeam` returns unless the store
  // is staged-dirty, `save` unless a draft exists), which is what lets the
  // pair be called unconditionally.
  const onSaveForTeam = useCallback((via: "button" | "hotkey") => {
    // Counted once the board commit lands; a clean board with only display
    // changes has no board commit, so it counts on the press.
    void saveToTeam().then((committed) => {
      if (committed || !dirty) trackWidget(WidgetViewEvent.SaveForTeam);
    });
    void viewSave?.(via);
  }, [trackWidget, saveToTeam, viewSave, dirty]);

  const onDiscardStaged = useCallback(() => {
    trackWidget(WidgetViewEvent.DiscardStagedChanges);
    discardChanges();
    viewDiscard?.();
  }, [trackWidget, discardChanges, viewDiscard]);

  // "Save as new tab": the staged edits become a brand-new saved dashboard
  // tab — the same fork-into-a-view path every saved view's Save button has.
  // Create the tab (server default-names it by type), write the working copy
  // onto its board BEFORE navigating (the first read would otherwise seed
  // the default template), then land on it; this board reverts to its shared
  // baseline. A failed board write keeps the working copy dirty for retry,
  // so navigation only happens on full success.
  const saveAsNewTab = useCallback(async () => {
    if (!views) return;
    const created = await views.createView(
      { type: "dashboard", config: { ...views.config } },
      "save_as_new_tab",
    );
    if (!created?.widgetView) return;
    if (!(await saveToNewView(created.widgetView))) return;
    trackWidget(WidgetViewEvent.SaveAsNewTab);
    replace(viewPath(views.scope, created));
  }, [views, trackWidget, saveToNewView, replace]);

  // A fork-holding writer's temporary jump to the shared board. Entering
  // forces staged mode past the fork; Done returns to it. The banner's Done
  // is disabled while staged edits are dirty (Save/Discard must resolve
  // first — a mode flip silently drops the staged working copy), and the
  // jump self-clears if the fork disappears under it (Reset from another
  // session): staged is then already the resting mode.
  const onEditTeamLayout = useCallback(() => {
    trackWidget(WidgetViewEvent.EditTeamLayout);
    setEditingTeamLayout(true);
  }, [trackWidget]);
  const onDoneEditingTeamLayout = useCallback(() => {
    trackWidget(WidgetViewEvent.ExitTeamLayout);
    setEditingTeamLayout(false);
  }, [trackWidget]);
  useEffect(() => {
    if (!hasFork) setEditingTeamLayout(false);
  }, [hasFork]);

  // The tooltip sentence names who the shared board reaches: Save here
  // writes for the whole audience, not for the viewer alone.
  const stagedAudience =
    scope.kind === "team"
      ? `everyone on ${teamMap.get(entityId)?.teamName ?? "the team"}`
      : "everyone who can see this dashboard";
  // Display-option edits are unsaved changes to this tab exactly like staged
  // widget edits, so they surface on the SAME control rather than growing a
  // second Save button beside it (`ViewSaveButton` stays the list/board
  // mount). Committing them is `create.views`, which a board writer need not
  // hold: display edits alone then mount no control at all (like
  // `ViewSaveButton`), and display edits beside a savable staged board keep
  // the control with Save disabled until the display edits are discarded.
  const viewOptionsDirty = inSavedView && (views?.dirtyKeys.length ?? 0) > 0;
  const canSaveViewOptions = Boolean(views?.canManage);
  // The toolbar's staged-save control: presence IS the unsaved indicator, so
  // it mounts only while dirty. The save DESTINATION is chosen after
  // editing — "Save as new tab" appears only inside a views scope for
  // viewers who may create tabs (a mount outside one gets a Discard-only
  // menu), and only with a staged board to carry: the
  // new tab's board is written from the working copy, and a display-only
  // edit has none to write, so it would seed the default template instead.
  const saveButton =
    dirty || (viewOptionsDirty && canSaveViewOptions) ? (
      <SaveViewControl
        canSave={!viewOptionsDirty || canSaveViewOptions}
        tooltip={`Save for ${stagedAudience}`}
        onSave={onSaveForTeam}
        onDiscard={onDiscardStaged}
        onSaveAsNewTab={
          dirty && views?.activeView && views.canManage
            ? () => {
                void saveAsNewTab();
              }
            : undefined
        }
      />
    ) : null;

  // The Customize view sections carry the board actions. The Reset row's
  // presence IS the "you're on your own copy" indicator (no badge, no
  // toggle), and Edit team layout appears only for a writer parked on a
  // fork; both hide during the jump itself. One node, two mounts: the
  // anchored `ViewOptionsPanel` inside a saved-view scope,
  // `FreeToolbarChrome`'s popover fallback outside one.
  const parkedOnFork =
    isSharedTier(access.tier) && !consumeOnly && !personalOnly && hasFork && !editingTeamLayout;
  const viewOptions = (
    <DashboardViewOptions
      canEdit={canEdit}
      scopeKind={scope.kind}
      autoRefresh={autoRefresh}
      onAutoRefreshChange={onAutoRefreshChange}
      autoLayout={autoLayout}
      onAutoLayoutChange={onAutoLayoutChange}
      onOpenWorkflows={
        // Only a workspace dashboard has a source set of its own to edit.
        views?.activeView?.scopeKind === "workspace"
          ? () => views.setPanelPage("workflows")
          : undefined
      }
      workflowCount={views?.activeView?.workflowIds.length ?? 0}
      onOpenFilters={
        // The panel's Filters page reads its field defs off the filter
        // provider and edits the view's draft, so it needs both: a saved
        // view to write and a provider to render from.
        views && issueFilters
          ? () => views.setPanelPage("filters")
          : undefined
      }
      filterCount={issueFilters?.viewFilterCount ?? 0}
      canManageView={views?.canManage ?? false}
      onResetToTeam={parkedOnFork ? onResetPersonal : undefined}
      onEditTeamLayout={parkedOnFork && access.canWrite ? onEditTeamLayout : undefined}
    />
  );

  // The personal home (My Work) is the one surface with a greeting line, and
  // so the one with a page-layout gear to hide it. Read for every viewer:
  // the setting is a display option of the page, not a board edit.
  const hasGreeting =
    isManaged && Boolean(title) && !tabLinkedChrome && scope.kind === "member";
  const greetingSetting = useMyWorkGreetingVisible();

  // ONE Manage-cards action opens the managed widget catalog. Masthead
  // actions sit on the 24px rung.
  //
  // Standalone (My Work): the labeled themed primary fill — the surface's one
  // action, matching the filled create action every other view's toolbar
  // leads with. The page-layout gear trails it behind a vertical rule: board
  // authoring first, then the viewer's own page setup, present even when the
  // board itself is read-only.
  //
  // Tab-connected (Team Overview): the team shell already carries the team's
  // filled primary action (Add member) on its masthead, so this band's
  // control is a settings gear on the quiet outline rung with a tooltip —
  // board authoring reads as the view's tool, not a second primary.
  const manageCards = canEdit ? (
    tabLinkedChrome ? (
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="outline"
              size="icon-xs"
              shape="square"
              aria-label="Manage cards"
              onClick={() => setAddOpen(true)}
            >
              <Icon name="settings" size="xs" />
            </Button>
          }
        />
        <TooltipPopup side="bottom">Manage cards</TooltipPopup>
      </Tooltip>
    ) : (
      <Button size="xs" shape="pill" onClick={() => setAddOpen(true)}>
        <Text size={null}>Manage cards</Text>
      </Button>
    )
  ) : null;
  const layoutGear = hasGreeting ? (
    <DashboardLayoutPopover
      greetingVisible={greetingSetting.visible}
      onGreetingVisibleChange={(visible: boolean) => {
        trackWidget(WidgetViewEvent.ToggleGreeting, { visible });
        greetingSetting.setVisible(visible);
      }}
    />
  ) : null;
  const mastheadActions =
    manageCards || layoutGear ? (
      <>
        {manageCards}
        {manageCards && layoutGear ? (
          <Separator orientation="vertical" className="h-4" />
        ) : null}
        {layoutGear}
      </>
    ) : undefined;

  const onDateRangeChange = useCallback(
    (next: typeof scopeState.dateRange) => {
      scopeState.setDateRange(next);
      trackWidget(WidgetViewEvent.FilterDate, { preset: next.key });
    },
    [scopeState, trackWidget],
  );

  // Board refresh: the host owns the state machine (`useWidgetRefresh`). Its
  // `tick` keys the boundary around the board area, so a refresh remounts the
  // grid and every widget body's `cache-and-network` query re-runs against
  // the server; the boundary reports that remount, and the hook watches the
  // queries it created until they settle before claiming success. The
  // toolbar's control (`dashboard-refresh-control.tsx`) only renders the
  // handle. `auto` says which path fired — a press or an auto-refresh tick —
  // so the manual metric stays unpolluted by a timer.
  const onRefreshBoard = useCallback(
    (auto: boolean) => {
      // Auto-refresh ticks are a timer, not a person: only presses count.
      if (auto) return;
      trackWidget(WidgetViewEvent.RefreshBoard, { auto });
    },
    [trackWidget],
  );
  const boardRefresh = useWidgetRefresh(onRefreshBoard, autoRefresh);

  const masthead =
    isManaged && title ? (
      <DashboardMastheadChrome
        title={title}
        dateRange={scopeState.dateRange}
        onDateRangeChange={onDateRangeChange}
        dateAudience={scope.kind === "member" ? "personal" : "team"}
        actions={mastheadActions}
        {...(tabLinkedChrome ? { connectedToTabs: true } : {})}
      />
    ) : null;

  // My Work's greeting scrolls with the board under the pinned header. The
  // switch slides it in and out through the library's collapse (height
  // transition, unmounted once closed) so the board glides up a line rather
  // than jumping; the panel opens at rest on first paint, so a saved "on"
  // never animates in. Off, nothing else moves: the board column carries
  // its own top inset, so the grid opens on the same padding it holds below
  // the greeting.
  const greeting = hasGreeting ? (
    <Collapsible open={greetingSetting.visible}>
      <CollapsiblePanel>
        <DashboardGreeting greeting={greetingLabel(currentUser?.name)} />
      </CollapsiblePanel>
    </Collapsible>
  ) : null;

  return (
    <DashboardScopeProvider value={scopeState.scope}>
      <WidgetDrilldownProvider value={drilldown}>
        {/* The toolbar band renders below the tab strip; the board paints the
          same page fill as the header above it, so the two read as one
          continuous surface and only the widget cards float. */}
        <Box direction="column" fill="flex" surface="background">
          {!isManaged &&
          (widgets.length > 0 ||
            initialLoading ||
            dirty ||
            viewOptionsDirty) ? (
            // `initialLoading` here is an uncached view switch or a transient
            // working-copy reset (the very first load full-page-returned
            // above): keep the toolbar — and any open popover in it — mounted
            // across the flip.
            // Either kind of unsaved change keeps it mounted when the board is
            // cleared to blank — the save control's decision must stay
            // reachable.
            <FreeToolbarChrome
              save={saveButton}
              options={viewOptions}
              scope={scope}
              dateRange={scopeState.dateRange}
              onDateRangeChange={onDateRangeChange}
              onlyMeSelected={scopeState.currentUser.isCurrentUserButtonSelected}
              onOnlyMePress={() => {
                // Mirror the toggle's own resolution so analytics reports the
                // state the press actually lands on (desktop always selects;
                // touch toggles), not a hardcoded `true`.
                const enabled = scopeState.currentUser.isDesktop
                  ? true
                  : !scopeState.currentUser.isCurrentUserButtonSelected;
                scopeState.currentUser.handleCurrentUserPress();
                trackWidget(WidgetViewEvent.ToggleOnlyMe, { level: "board", enabled });
              }}
              onOnlyMeClear={() => {
                scopeState.currentUser.handleCurrentUserClear();
                trackWidget(WidgetViewEvent.ToggleOnlyMe, { level: "board", enabled: false });
              }}
              canEdit={canEdit}
              consumeOnly={consumeOnly}
              onAddWidget={() => setAddOpen(true)}
              refresh={boardRefresh}
            />
          ) : null}

          {/* The Edit-team-layout jump's banner — the "you are not on your
            own copy right now" marker, holding under the toolbar with the
            Done exit. Done disables while staged edits are dirty: the save
            control must resolve them first. */}
          {!isManaged && editingTeamLayout ? (
            <DashboardTeamLayoutBanner
              scopeKind={scope.kind}
              dirty={dirty}
              onDone={onDoneEditingTeamLayout}
            />
          ) : null}

          {/* Both masthead chromes mount OUTSIDE the scroller, so they hold at
            the top of the pane while the board travels under them. My Work's
            greeting is content, not chrome — it scrolls with the board. */}
          {masthead}

          {/* The board's own scroller — its scrollbar is user-visible chrome, and
            the grid finds it by walking up to the nearest overflow ancestor
            (`getScrollParent`), so ScrollArea's viewport serves it directly. */}
          <Box direction="column" grow minHeight={0}>
            <ScrollArea scrollFade>
              {greeting}
              <Box
                direction="column"
                align={isManaged && title ? "center" : undefined}
                paddingTop={isManaged && title ? "lg" : "md"}
                paddingX={{ base: "sm", md: isManaged && title ? "lg" : "md" }}
                paddingBottom="xl"
                minHeight="100%"
              >
                <Box
                  direction="column"
                  fill="width"
                  maxWidth={isManaged && title ? MANAGED_HOME_CONTENT_MAX_WIDTH : undefined}
                >
                  {/* Keyed on the refresh tick: a refresh remounts everything
                      inside, and the boundary reports the remount so the hook
                      can watch the re-run queries settle. It wraps every board
                      state, so a press on an empty board still settles. */}
                  <WidgetRefreshBoundary
                    key={boardRefresh.tick}
                    onMounted={boardRefresh.onRemounted}
                  >
                    {initialLoading ? (
                      // The first load, an uncached view switch or a transient
                      // working-copy reset. The stand-in is an ANONYMOUS board
                      // silhouette: the real tile geometry lives in the
                      // server's rows, so the tiles promise only the grid's
                      // rhythm, never a layout. Delayed, so a cached view
                      // switch or a warm answer never paints a tile at all.
                      // The chrome around it stays mounted, so the
                      // masthead/toolbar — and any open popover in them —
                      // survive the flip; and nothing empty-state paints
                      // before the answer, so a shared board never flashes a
                      // chooser or CTA it does not own.
                      <DashboardBoardSkeleton />
                    ) : widgets.length === 0 ? (
                      // An empty board is always DELIBERATE now — the server seeds
                      // a new view's default board on its first read, so this state
                      // only exists after someone cleared it (or emptied their
                      // personal fork). Editors get the template chooser back —
                      // exactly the "pick a starting point" moment a cleared board
                      // asks for; viewers get the honest empty state.
                      //
                      // Personal mode is editable for everyone, so it needs one
                      // more cut: a non-writer WITHOUT a fork is looking at the
                      // shared board itself, and an empty one means its owner has
                      // not set it up. The chooser would read as "set up the
                      // team's dashboard" — a pick they cannot make — and would
                      // fork a copy that diverges the moment the owner builds
                      // the real board. They get the owner-facing empty state
                      // instead. A non-writer WITH a fork emptied their own copy;
                      // that board is theirs to restart, so the chooser stays.
                      canEdit &&
                      !isManaged &&
                      !(mode === "personal" && !access.canWrite && !hasFork) ? (
                        <DashboardTemplateChooser
                          onSelectTemplate={onSelectTemplate}
                          onStartFromScratch={onStartFromScratch}
                        />
                      ) : (
                        <Box direction="column" paddingY="2xl">
                          <WidgetEmpty
                            size="default"
                            title="No widgets yet"
                          />
                        </Box>
                      )
                    ) : (
                      <WidgetGrid
                        // The board refresh remounts the grid (the boundary above
                        // is keyed on the refresh tick): every widget body's
                        // cache-and-network query re-runs against the server — the
                        // same mechanism the detail dialog uses per widget.
                        instances={widgets}
                        rowHeight={DASHBOARD_ROW_HEIGHT}
                        gap={DASHBOARD_GRID_GAP}
                        defaultRows={DEFAULT_WIDGET_ROWS}
                        // Widgets float one surface above the board's page fill.
                        // Canvas-pinned widgets still mask the authoring guides
                        // with the board's own color.
                        surface="elevated"
                        canvas="background"
                        canManage={gridCanManage}
                        canArrange={canEdit}
                        consumeOnly={consumeOnly}
                        // Dense flow IS the Auto-layout switch: the grid keeps
                        // the board packed through every gesture while it's on.
                        flow={autoLayout ? "dense" : undefined}
                        widthOptions={
                          canEdit && halfFullWidths ? HALF_FULL_WIDTH_OPTIONS : undefined
                        }
                        onMove={onMove}
                        onLayoutChange={isManaged ? undefined : history.handleLayoutChange}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                        onReplace={isManaged ? undefined : onReplace}
                        onConvertToCustom={canEdit ? onConvertToCustom : undefined}
                        onMaximize={onMaximize}
                        onConfigure={onConfigure}
                        onRename={(id, title) => onConfigChange(id, { title })}
                        // Body-level config persistence (a note's content, a column
                        // resize) is an edit — only wire it when the viewer may edit
                        // this board. A read-only viewer gets `undefined`, which is
                        // also THE signal an inline-editable body (the note) reads to
                        // render read-only. My Work's owner is a writer (private-owner
                        // needs no `widgets.manage`), so their note stays editable.
                        onConfigChange={canEdit ? onConfigChange : undefined}
                        areaAdd={isManaged || !canEdit ? undefined : areaAdd}
                        renderCard={isManaged ? renderManagedCard : undefined}
                      />
                    )}
                  </WidgetRefreshBoundary>
                </Box>
              </Box>
            </ScrollArea>
          </Box>

          <WidgetDetailDialog
            instance={maximizedInstance}
            open={maximizedInstance !== null}
            // The RIGHT, not the mode: maximizing a widget to tweak its
            // settings is a deliberate act and needs no board edit mode.
            canManage={canManageWidgets}
            onOpenChange={(o) => {
              if (!o) setMaximizedId(null);
            }}
            onPrevious={
              maximizedIndex > 0
                ? () => overlays.stepMaximized(at(widgets, maximizedIndex - 1, "widget").id)
                : undefined
            }
            onNext={
              maximizedIndex >= 0 && maximizedIndex < widgets.length - 1
                ? () => overlays.stepMaximized(at(widgets, maximizedIndex + 1, "widget").id)
                : undefined
            }
            onDuplicate={onDuplicate}
            onConvertToCustom={canEdit ? onConvertToCustom : undefined}
            onDelete={onDelete}
            onConfigChange={onConfigChange}
            dataSlice={dataSlice}
            dataRequestId={dataRequestId}
            chartRequestId={chartRequestId}
            onDataSliceChange={setDataSlice}
            settingsRequestId={settingsRequestId}
          />
        </Box>
      </WidgetDrilldownProvider>

      {!managedCatalog ? (
        // The saved-view Customize view panel, carrying this dashboard's
        // sections. Inside a views scope the toolbar gear opens it anchored
        // to the view pane; outside one (no ViewsProvider) it
        // renders null and FreeToolbarChrome's popover fallback serves the
        // same sections instead.
        <ViewOptionsPanel anchorId={VIEW_PANE_ANCHOR_ID} sections={viewOptions} />
      ) : null}

      {managedCatalog ? (
        <ManageWidgetsPanel
          open={addOpen}
          onOpenChange={setAddOpen}
          catalog={managedCatalog}
          instances={widgets}
          onToggle={onToggleManaged}
        />
      ) : (
        <AddWidgetDialog
          open={addOpen || replaceId !== null}
          onOpenChange={(o) => {
            if (o) return;
            // The scratch write-through covers only the gallery the scratch
            // pick opened; a later toolbar add is staged as usual.
            scratchViewRef.current = null;
            setAddOpen(false);
            setReplaceId(null);
            setMarqueeArea(null);
          }}
          title={replaceId ? "Replace Widget" : undefined}
          onSelect={(preset) => {
            if (replaceId) {
              trackWidget(WidgetViewEvent.ReplaceWidget, {
                from: widgets.find((i) => i.id === replaceId)?.type,
                to: preset.type,
                preset: preset.key,
                section: preset.section,
              });
              history.snapshot();
              replaceWidget(replaceId, preset);
              setReplaceId(null);
            } else {
              // A marquee-opened gallery places the pick at the drawn rect;
              // the toolbar-opened one lands in the first free slot.
              trackWidget(WidgetViewEvent.AddWidget, {
                type: preset.type,
                preset: preset.key,
                section: preset.section,
                ...(marqueeArea ? { via: "marquee" } : {}),
              });
              history.snapshot();
              if (!addFromScratch(preset, marqueeArea ?? undefined)) {
                addWidget(preset, marqueeArea ?? undefined);
              }
              setMarqueeArea(null);
            }
            setAddOpen(false);
          }}
        />
      )}

    </DashboardScopeProvider>
  );
}
